import {
  BookmarkSimple, ArrowSquareOut, ChartLineUp, MagnifyingGlass, CurrencyBtc, Coin, Gauge, Globe
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const RESOURCES = [
  {
    name: "DexScreener",
    url: "https://dexscreener.com",
    description: "Real-time on-chain DEX trading pair charts, volume, liquidity, and token discovery across every major chain.",
    tag: "DEX CHARTS",
    icon: ChartLineUp,
    color: "emerald",
  },
  {
    name: "DEXTools",
    url: "https://www.dextools.io",
    description: "Deep on-chain trading analytics, token auditing, honeypot detection, and multi-chain pair tracking.",
    tag: "DEX ANALYTICS",
    icon: Gauge,
    color: "blue",
  },
  {
    name: "CoinMarketCap",
    url: "https://coinmarketcap.com",
    description: "Industry-standard crypto data: prices, market cap, volume, exchange listings, and trending coins.",
    tag: "MARKET DATA",
    icon: CurrencyBtc,
    color: "amber",
  },
  {
    name: "CoinGecko",
    url: "https://www.coingecko.com",
    description: "Independent crypto tracker with deep historical data, developer metrics, community stats, and exchange volume.",
    tag: "MARKET DATA",
    icon: Coin,
    color: "rose",
  },
  {
    name: "CoinCodex",
    url: "https://coincodex.com",
    description: "Price predictions, technical analysis, historical ROI stats, and market-cap comparisons for 20,000+ coins.",
    tag: "FORECASTS",
    icon: MagnifyingGlass,
    color: "violet",
  },
  {
    name: "PLS Start.me Dashboard",
    url: "https://start.me/p/gGQ09M/plstart-me",
    description: "Curated start page with crypto resources, tools, charts, and community links. PulseChain-focused.",
    tag: "LINK HUB",
    icon: Globe,
    color: "teal",
  },
];

const COLOR_MAP = {
  emerald: { bg: "bg-emerald-900/20", border: "border-emerald-800/30", text: "text-emerald-400", hover: "hover:border-emerald-700/50", grouphover: "group-hover:text-emerald-400", badge: "bg-emerald-900/30 text-emerald-400" },
  blue: { bg: "bg-blue-900/20", border: "border-blue-800/30", text: "text-blue-400", hover: "hover:border-blue-700/50", grouphover: "group-hover:text-blue-400", badge: "bg-blue-900/30 text-blue-400" },
  amber: { bg: "bg-amber-900/20", border: "border-amber-800/30", text: "text-amber-400", hover: "hover:border-amber-700/50", grouphover: "group-hover:text-amber-400", badge: "bg-amber-900/30 text-amber-400" },
  rose: { bg: "bg-rose-900/20", border: "border-rose-800/30", text: "text-rose-400", hover: "hover:border-rose-700/50", grouphover: "group-hover:text-rose-400", badge: "bg-rose-900/30 text-rose-400" },
  violet: { bg: "bg-violet-900/20", border: "border-violet-800/30", text: "text-violet-400", hover: "hover:border-violet-700/50", grouphover: "group-hover:text-violet-400", badge: "bg-violet-900/30 text-violet-400" },
  teal: { bg: "bg-teal-900/20", border: "border-teal-800/30", text: "text-teal-400", hover: "hover:border-teal-700/50", grouphover: "group-hover:text-teal-400", badge: "bg-teal-900/30 text-teal-400" },
};

export default function MiscLinks() {
  return (
    <div data-testid="resources-page">
      <div className="sticky top-0 z-20 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 px-3 md:px-5 py-3 md:py-4">
        <div className="flex items-center gap-2 md:gap-2.5">
          <BookmarkSimple size={20} weight="bold" className="text-amber-400 shrink-0" />
          <h1 className="font-heading text-lg md:text-2xl font-bold text-zinc-50 tracking-tight">Resources</h1>
          <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-amber-900/30 text-amber-400 h-4">EXTERNAL</Badge>
        </div>
        <p className="text-[10px] md:text-xs text-zinc-500 mt-1">Curated crypto research, charting, and market-data tools</p>
      </div>

      <div className="p-3 md:p-5 max-w-[1400px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {RESOURCES.map((r) => {
            const Icon = r.icon;
            const c = COLOR_MAP[r.color];
            return (
              <a
                key={r.name}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group block bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-4 md:p-5 transition-colors ${c.hover}`}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-sm ${c.bg} border ${c.border} flex items-center justify-center shrink-0`}>
                    <Icon size={20} weight="bold" className={c.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className={`text-sm md:text-base font-heading font-bold text-zinc-100 transition-colors ${c.grouphover}`}>{r.name}</h2>
                      <Badge className={`text-[7px] px-1.5 py-0.5 border-0 rounded-sm h-3.5 ${c.badge}`}>{r.tag}</Badge>
                      <ArrowSquareOut size={12} className={`text-zinc-600 transition-colors ${c.grouphover} ml-auto`} />
                    </div>
                    <p className="text-[10px] md:text-[11px] text-zinc-600 font-data mt-0.5 truncate">{r.url.replace(/^https?:\/\//, "")}</p>
                    <p className="text-[11px] md:text-xs text-zinc-400 leading-relaxed mt-2">{r.description}</p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <p className="text-[9px] text-zinc-700 text-center mt-6">
          All links open in a new tab. The Golden Nest does not endorse or verify third-party content.
        </p>
      </div>
    </div>
  );
}
